// A class assignment's rules as the student reads them. The teacher's Assign Practice
// preview renders this same component, so the preview cannot drift from what the student
// is shown on My Assignments.
import React from 'react';
import { useTranslation } from 'react-i18next';
import type { AssignmentRules } from '../../services/assignmentService';

interface Props {
  rules: AssignmentRules;
  dueDate?: string | null;
  className?: string | null;
}

const rowStyle: React.CSSProperties = { display: 'flex', gap: 8, fontSize: '0.85rem', margin: '0 0 6px' };
const labelStyle: React.CSSProperties = { fontWeight: 600, color: '#475569', minWidth: 120 };
const blockStyle: React.CSSProperties = { fontSize: '0.85rem', margin: '0 0 8px', whiteSpace: 'pre-wrap' };

const AssignmentRulesSummary: React.FC<Props> = ({ rules, dueDate, className }) => {
  const { t } = useTranslation();
  return (
    <div data-testid="assignment-rules">
      {className && (
        <p style={rowStyle}>
          <span style={labelStyle}>{t('assignmentRules.class')}</span>
          <span>{className}</span>
        </p>
      )}
      {rules.practice_type && (
        <p style={rowStyle}>
          <span style={labelStyle}>{t('assignmentRules.practiceType')}</span>
          <span>{t(`practiceTypes.${rules.practice_type}`)}</span>
        </p>
      )}
      <p style={rowStyle}>
        <span style={labelStyle}>{t('assignmentRules.due')}</span>
        <span>{dueDate ? new Date(dueDate).toLocaleString() : t('assignmentRules.noDueDate')}</span>
      </p>
      <p style={rowStyle}>
        <span style={labelStyle}>{t('assignmentRules.attempts')}</span>
        <span>
          {rules.max_attempts ? t('assignmentRules.attemptsCount', { count: rules.max_attempts }) : t('assignmentRules.unlimited')}
        </span>
      </p>
      {rules.session_minutes ? (
        <p style={rowStyle}>
          <span style={labelStyle}>{t('assignmentRules.sessionLength')}</span>
          <span>{t('assignmentRules.minutes', { count: rules.session_minutes })}</span>
        </p>
      ) : null}
      <p style={rowStyle}>
        <span style={labelStyle}>{t('assignmentRules.hints')}</span>
        <span>{rules.hints_allowed ? t('assignmentRules.hintsAllowed') : t('assignmentRules.hintsNotAllowed')}</span>
      </p>
      {rules.objectives.length > 0 && (
        <>
          <p style={{ ...rowStyle, marginTop: 10 }}>
            <span style={labelStyle}>{t('assignmentRules.objectives')}</span>
          </p>
          <ul style={{ margin: '0 0 8px', paddingLeft: '1.2rem', fontSize: '0.85rem' }}>
            {rules.objectives.map((objective, index) => (
              <li key={index}>{objective}</li>
            ))}
          </ul>
        </>
      )}
      {/* The scale stands on its own: a teacher may pick one without writing rubric text. */}
      {rules.rubric_max_score ? (
        <p style={rowStyle}>
          <span style={labelStyle}>{t('assignmentRules.scoring')}</span>
          <span>{t('assignmentRules.scoredOutOf', { max: rules.rubric_max_score })}</span>
        </p>
      ) : null}
      {rules.rubric && (
        <>
          <p style={{ ...rowStyle, marginTop: 10 }}>
            <span style={labelStyle}>{t('assignmentRules.rubric')}</span>
          </p>
          <p style={blockStyle}>{rules.rubric}</p>
        </>
      )}
      {rules.accommodations && (
        <>
          <p style={{ ...rowStyle, marginTop: 10 }}>
            <span style={labelStyle}>{t('assignmentRules.accommodations')}</span>
          </p>
          <p style={blockStyle}>{rules.accommodations}</p>
        </>
      )}
    </div>
  );
};

export default AssignmentRulesSummary;
